import { css } from '@bts-web/utils-style-engine';
import { NavigationBar } from '../../common/components/NavigationBar/NavigationBar';
import { NavigationBackButton } from '../../navigation/NavigationBackButton/NavigationBackButton.use-client';
import { OrderForSummary, OrderOperation } from '../../orders/types';
import { CancelTradeWithConfirmButton } from '../Reusable/CancelTradeConfirmationModal/CancelTradeWithConfirmButton';
import { AcceptOrderTranslations } from './getTranslations';
import { AssetImage } from '../../common/components/AssetImage/AssetImage.use-client';
import { Locale } from '@bts-web/utils-lokalise';
import {
  intlAssetValueFormatting,
  intlFiatValueFormatting,
  MaximumFractionDigits,
} from '@bts-web/utils-formatting';
import { getAppConfig } from '@bts-web/core-app-config';
import {
  AmountInputWrapper,
  BelowInputInfoTable,
  Collapsible,
} from '../../common/components';
import { SummaryInfo } from './SummaryInfo';
import { CreateOrderButton } from './CreateOrderButton';
import { GenericErrorMessage } from '../../layouts/GenericErrorMessage/GenericErrorMessage';
import { PoweredBy } from '../Reusable/PoweredBy/PoweredBy';
import { isBpfsAsset } from '../utils';
import { STOP_ORDER_VALIDITY_MONTHS } from '../utils/constants';
import { AssetType } from '@bts-web/core-features/asset-details';

interface OrderSummaryProps {
  translations: AcceptOrderTranslations;
  order: OrderForSummary;
  locale: Locale;
  t: (key: string, options?: any) => string;
}

const infoRowTitleStyle = css({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  color: 'neutrals.text_secondary',
  fontSize: 'body.medium',
  fontWeight: 'body.medium',
  letterSpacing: 'body.medium',
  lineHeight: 'body.medium',
  textAlign: 'start',
});

const headlineStyle = css({
  fontSize: 'headline.small_medium',
  fontWeight: 'headline.small_medium',
  lineHeight: 'headline.small_medium',
});

export const OrderSummary = ({
  translations,
  order,
  locale,
  t,
}: OrderSummaryProps) => {
  const asset = order?.asset;

  if (!order || !asset) {
    return <GenericErrorMessage />;
  }

  const isBuy = order.operation === OrderOperation.BUY;

  const { currency } = getAppConfig();

  const price = asset.price;

  const orderAmountValue = isBuy
    ? order.fiatAmount?.value || '0'
    : order.assetAmount?.value || '0';

  const formatFiat = (value: string | number, fractionDigits?: number) =>
    intlFiatValueFormatting(value, { locale, currency, fractionDigits });

  const formatAsset = (value: string | number, fractionDigits?: number) =>
    intlAssetValueFormatting(value, { locale, fractionDigits });

  const secondaryAmountFormatted = isBuy
    ? formatAsset(order.assetAmount?.value || 0, MaximumFractionDigits.ASSET)
    : formatFiat(order.fiatAmount?.value || 0, order.fiatAmount?.precision);

  const totalFormatted = isBuy
    ? formatFiat(orderAmountValue)
    : `${formatAsset(orderAmountValue, MaximumFractionDigits.ASSET)} ${asset.symbol}`;

  const collapsibleItems = [
    {
      title: isBuy ? translations.buyAmount : translations.sellAmount,
      value: isBuy
        ? formatFiat(orderAmountValue)
        : `${formatAsset(orderAmountValue, MaximumFractionDigits.ASSET)} ${asset.symbol}`,
    },
    {
      title: translations.expectedFees,
      value: formatFiat(order.fee?.value || 0),
    },
    {
      title: translations.currentPrice,
      value: `1 ${asset.symbol} ≈ ${formatFiat(price?.value || 0)}`,
    },
    {
      title: (
        <div className={infoRowTitleStyle}>
          {translations.fulfillmentPriceTitle}
          <SummaryInfo
            translations={translations}
            title={translations.fulfillmentInfoTitle}
            subtitle1={translations.fulfillmentInfoSubtitle1}
            subtitle2={translations.fulfillmentInfoSubtitle2}
          />
        </div>
      ),
      value: translations.fulfillmentPriceSubtitle,
    },
    {
      title: (
        <div className={infoRowTitleStyle}>
          {translations.maximumPriceDeviation}
          <SummaryInfo
            title={translations.priceDeviationTitle}
            translations={translations}
            subtitle1={translations.priceDeviationInfoSubtitle1}
            subtitle2={t('stop_order_note_deviation_subtitle_2', {
              amount: `${order.slippagePercentage}%`,
            })}
          />
        </div>
      ),
      value: t('stop_order_max_price_deviation_subtitle', {
        amount: `${order.slippagePercentage}%`,
      }),
    },
    { title: '' },
    { title: translations.priceDisclaimer },
  ];

  return (
    <>
      <NavigationBar
        showBorderBottom
        title={translations.summary}
        NavigationLeftSlot={<NavigationBackButton />}
        NavigationRightSlot={
          <CancelTradeWithConfirmButton translations={translations} />
        }
      />

      <div
        className={css({
          display: 'flex',
          flexDirection: 'column',
          px: 'medium',
          height: '100%',
          gap: 'extra_small',
        })}
      >
        {/* Asset & Target Price */}
        <div
          data-testid="asset-price-info"
          data-element="details-container"
          className={css({
            display: 'flex',
            alignItems: 'center',
            py: 'small',
            gap: 'extra_small',
            width: '100%',
          })}
        >
          <div
            className={css({
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontWeight: '500',
              fontSize: 'title.medium_medium',
              overflow: 'hidden',
              width: '100%',
              '& img': { flexShrink: 0 },
              '& > *': { lineClamp: 2 },
            })}
          >
            <AssetImage
              width={24}
              height={24}
              src={asset.logoUrl as string}
              alt={asset.symbol as string}
              aria-label="asset image"
              placeholder="empty"
            />
            <span>{asset.symbol}</span>
          </div>

          <div
            data-element="target-price-info"
            className={css({
              color: 'neutrals.text_primary',
              fontSize: 'caption.medium_medium',
              display: 'flex',
              flex: 1,
              alignItems: 'center',
              gap: 'extra_small_2',
              '& > *': { whiteSpace: 'nowrap' },
            })}
          >
            <span
              className={css({
                flex: 1,
                display: 'flex',
                justifyContent: 'flex-end',
              })}
            >
              {translations.targetPrice}
            </span>
            <span>=</span>
            <span>
              {intlFiatValueFormatting(order.targetPrice?.value || 0, {
                locale,
                notation: 'standard',
                currency,
                fractionDigits: order.targetPrice?.precision,
              })}
            </span>
          </div>
        </div>

        {/* Amount Input & Fee Info */}
        <AmountInputWrapper
          inputContextData={{
            symbol: asset.symbol || '',
            currency,
            displayType: isBuy ? 'FIAT' : 'ASSET',
            value: orderAmountValue,
            placeholder: undefined,
          }}
        />
        <span
          className={css({
            fontFamily: 'body.medium_medium',
            fontSize: 'body.medium_medium',
            fontWeight: 'body.medium_medium',
            lineHeight: 'body.medium_medium',
            letterSpacing: 'body.medium_medium',
            color: 'neutrals.text_primary',
          })}
        >
          {t('stop_order_inc_fees_at_target_price' as 'stop_order_inc_fees', {
            amount: secondaryAmountFormatted,
            ticker: isBuy ? asset.symbol : '',
            defaultValue: '≈{{amount}} {{ticker}} inc. fees at target price',
          })}
        </span>

        {/* Collapsible Order Terms */}
        <Collapsible
          className={css({
            borderBottomWidth: '1px',
            borderBottomColor: 'neutrals.divider',
            paddingBottom: 'extra_small',
          })}
          header={{
            LeftContentIndicatorSlot: (
              <span className={css({ display: 'flex', gap: 'extra_small_3' })}>
                <span>{translations.showLimitOrderTerms}</span>
              </span>
            ),
          }}
        >
          <BelowInputInfoTable items={collapsibleItems} />
        </Collapsible>

        <div
          className={css({
            display: 'flex',
            justifyContent: 'space-between',
            borderBottomWidth: '1px',
            borderBottomColor: 'neutrals.divider',
            paddingBottom: 'medium',
            paddingTop: 'extra_small',
          })}
        >
          <span>{translations.orderIsActiveFor}</span>
          <span>
            {t('stop_order_valid_months', {
              amount: STOP_ORDER_VALIDITY_MONTHS,
            })}
          </span>
        </div>

        {/* Footer: Powered By, Total, Accept */}
        <div
          className={css({
            marginTop: 'auto',
            paddingBottom: 'medium',
          })}
        >
          <PoweredBy
            value={
              isBpfsAsset(order.asset.__typename as AssetType)
                ? translations.poweredByBpfs
                : translations.poweredByTenant
            }
            showLogo={!isBpfsAsset(order.asset.__typename as AssetType)}
          />

          <div
            className={css({
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              paddingBottom: 'small',
            })}
          >
            <span
              className={`${headlineStyle} ${css({ color: 'neutrals.text_secondary' })}`}
            >
              {translations.total}
            </span>
            <span
              className={`${headlineStyle} ${css({ color: 'neutrals.text_primary' })}`}
            >
              {totalFormatted}
            </span>
          </div>

          <CreateOrderButton
            assetId={asset.id}
            orderId={order.id}
            translations={translations}
            operation={order.operation as OrderOperation}
          />
        </div>
      </div>
    </>
  );
};
